"use client";

import { useCallback, useState } from "react";

export type ImageLoadStatus = "loading" | "loaded" | "error";

/**
 * Whether an image has arrived. The status belongs to one `src`: a new source starts as loading
 * without an effect. An image the browser already had (cache, or loaded before hydration) is
 * caught by the ref on mount, so it never flashes a placeholder.
 */
export function useImageLoad(src: string) {
  const [state, setState] = useState<{ src: string; status: ImageLoadStatus }>({
    src,
    status: "loading",
  });
  const status: ImageLoadStatus = state.src === src ? state.status : "loading";

  const ref = useCallback(
    (image: HTMLImageElement | null) => {
      // `complete` with no pixels can also mean "not requested yet" (lazy), so only a decoded
      // image counts; failures still arrive through `onError`.
      if (image?.complete && image.naturalWidth > 0) setState({ src, status: "loaded" });
    },
    [src],
  );
  const onLoad = useCallback(() => setState({ src, status: "loaded" }), [src]);
  const onError = useCallback(() => setState({ src, status: "error" }), [src]);

  return { status, ref, onLoad, onError };
}
